import React, { useRef, useState } from "react";

import { EllipsisVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

import AlertDialog from "@/src/components/dialog/AlertDialog";
import { Button } from "@/src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import type { JSX } from "react";

import type { LoadedActions, RowAction } from "./dataTable.types";

type DataTableRowActionsProps = {
  rowLabel: string;
} & (
  | { actions: RowAction[]; loadActions?: never }
  | { loadActions: () => Promise<RowAction[]>; actions?: never }
);

function DataTableRowActions({
  actions,
  loadActions,
  rowLabel,
}: DataTableRowActionsProps): JSX.Element | null {
  const { t } = useTranslation("table");
  const [pendingAction, setPendingAction] = useState<RowAction | null>(null);
  const [loadedActions, setLoadedActions] = useState<LoadedActions>("loading");
  const lastLoadRef = useRef(0);

  const currentActions = actions ?? loadedActions;
  const visibleActionList = Array.isArray(currentActions)
    ? currentActions.filter((action) => !action.hidden)
    : [];

  if (actions && visibleActionList.length === 0) return null;

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen || !loadActions) return;

    lastLoadRef.current += 1;
    const load = lastLoadRef.current;
    setLoadedActions("loading");
    loadActions().then(
      (loaded) => {
        if (load === lastLoadRef.current) setLoadedActions(loaded);
      },
      () => {
        if (load === lastLoadRef.current) setLoadedActions("error");
      },
    );
  };

  const handleSelect = (action: RowAction): void => {
    if (action.confirm) {
      setPendingAction(action);
      return;
    }
    action.onClick();
  };

  const handleConfirm = (): void => {
    pendingAction?.onClick();
    setPendingAction(null);
  };

  const handleConfirmOpenChange = (isOpen: boolean): void => {
    if (!isOpen) setPendingAction(null);
  };

  return (
    // Les événements React traversent les portails : ce wrapper arrête tout clic (molette comprise) avant la ligne.
    <div
      className="contents"
      onAuxClick={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      role="presentation"
    >
      {/* Non modal : le clic à côté ferme le menu et agit en même temps. */}
      <DropdownMenu modal={false} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={t("dataTable.rowActions", { row: rowLabel })}
            size="icon-sm"
            variant="ghost"
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {currentActions === "loading" && (
            <DropdownMenuItem disabled>
              {t("dataTable.loadingActions")}
            </DropdownMenuItem>
          )}
          {currentActions === "error" && (
            <DropdownMenuItem disabled>
              {t("dataTable.actionsError")}
            </DropdownMenuItem>
          )}
          {visibleActionList.map((action) => {
            const item = (
              <DropdownMenuItem
                disabled={action.disabled}
                onSelect={() => handleSelect(action)}
                variant={action.variant ?? "default"}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            );

            // Un item désactivé a pointer-events:none : l'infobulle doit venir du wrapper.
            if (action.disabled && action.tooltip) {
              return (
                <div
                  key={action.label}
                  data-tooltip-content={action.tooltip}
                  data-tooltip-id={APP_TOOLTIP_ID}
                >
                  {item}
                </div>
              );
            }
            return <React.Fragment key={action.label}>{item}</React.Fragment>;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {pendingAction?.confirm ? (
        <AlertDialog
          isOpen
          cancelLabel={t("dataTable.cancel")}
          confirmVariant={pendingAction.variant}
          description={pendingAction.confirm.description}
          onConfirm={handleConfirm}
          onOpenChange={handleConfirmOpenChange}
          title={pendingAction.confirm.title}
          confirmLabel={
            pendingAction.confirm.confirmLabel ?? t("dataTable.confirm")
          }
        />
      ) : null}
    </div>
  );
}

export default DataTableRowActions;
